/* eslint-disable class-methods-use-this */
/**
 * AnnotationManager - Global manager for annotation interactions
 *
 * Provides:
 * - Registration and tracking of annotation controllers
 * - Global annotation operations (close all, find by ID, etc.)
 * - Cursor integration helpers
 * - User-created annotation system (create, edit, save, remove)
 */
export class AnnotationManager {
  constructor() {
    this.annotations = new Map() // ID -> controller mapping
    this.activeAnnotations = new Set() // Track open annotations

    // User-created annotation system
    this.mode = 'view' // 'view' or 'annotate'
    this.userAnnotations = new Map() // ID -> annotation data mapping
    this.maxAnnotations = 20
    this.annotationIdCounter = 1
    this.isInitialized = false

    // Bind methods for event listeners
    this.handlePageClick = this.handlePageClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleAnnotationClick = this.handleAnnotationClick.bind(this)
  }

  /**
   * Initialize the annotation manager
   * This should be called explicitly to set up event listeners and global references
   */
  initialize() {
    if (this.isInitialized) {
      return this
    }

    // Add global event listeners
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('click', this.handleAnnotationClick, true)

    // Make globally available
    window.annotationManager = this
    // Also make available as userAnnotationManager for backward compatibility
    window.userAnnotationManager = this

    this.isInitialized = true
    return this
  }

  /**
   * Cleanup method to remove event listeners
   */
  destroy() {
    if (!this.isInitialized) return

    // Remove event listeners
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('click', this.handleAnnotationClick, true)
    document.removeEventListener('click', this.handlePageClick, true)

    // Clear global references
    if (window.annotationManager === this) {
      window.annotationManager = null
    }
    if (window.userAnnotationManager === this) {
      window.userAnnotationManager = null
    }

    this.isInitialized = false
  }

  /**
   * Register an annotation controller
   * @param {Object} controller - Annotation controller instance
   */
  register(controller) {
    if (controller.idValue) {
      this.annotations.set(controller.idValue, controller)
    }
  }

  /**
   * Unregister an annotation controller
   * @param {Object} controller - Annotation controller instance
   */
  unregister(controller) {
    if (controller.idValue) {
      this.annotations.delete(controller.idValue)
      this.activeAnnotations.delete(controller)
    }
  }

  /**
   * Find annotation controller by ID
   * @param {string} id - Annotation ID
   * @returns {Object|null} - Controller or null
   */
  findById(id) {
    return this.annotations.get(id) || null
  }

  /**
   * Find annotation controller by element
   * @param {HTMLElement} element - Element to search from
   * @returns {Object|null} - Controller or null
   */
  findByElement(element) {
    // Check if element is an annotation
    if (element.hasAttribute('data-annotation-id')) {
      const id = element.getAttribute('data-annotation-id')
      return this.findById(id)
    }

    // Check if element is inside an annotation
    const annotationElement = element.closest('[data-annotation-id]')
    if (annotationElement) {
      const id = annotationElement.getAttribute('data-annotation-id')
      return this.findById(id)
    }

    return null
  }

  /**
   * Close all annotations except the specified one
   * @param {Object} except - Controller to exclude from closing
   */
  closeAll(except = null) {
    // Close registered controller annotations
    this.activeAnnotations.forEach((controller) => {
      if (controller !== except && controller.isOpen()) {
        controller.close()
      }
    })

    // Also close any user annotations directly via DOM
    const openUserAnnotations = document.querySelectorAll('.annotation--active')
    openUserAnnotations.forEach((annotation) => {
      const annotationId = annotation.getAttribute('data-annotation-id')
      if (annotationId && annotationId.startsWith('user-annotation-')) {
        const controller = this.findById(annotationId)
        if (!controller || controller !== except) {
          annotation.classList.remove('annotation--active')
          this.handleAnnotationClose(annotationId)
        }
      }
    })
  }

  /**
   * Get all visible annotations
   * @returns {Array} - Array of visible annotation controllers
   */
  getVisibleAnnotations() {
    return Array.from(this.annotations.values()).filter((controller) => controller.isVisible())
  }

  /**
   * Get all hidden annotations
   * @returns {Array} - Array of hidden annotation controllers
   */
  getHiddenAnnotations() {
    return Array.from(this.annotations.values()).filter((controller) => !controller.isVisible())
  }

  /**
   * Reveal annotation by ID (cursor integration)
   * @param {string} id - Annotation ID
   * @param {Object} options - Reveal options
   * @returns {Promise} - Promise that resolves when revealed
   */
  async revealAnnotation(id, options = {}) {
    const controller = this.findById(id)
    if (controller) {
      controller.reveal(options)
      return new Promise((resolve) => {
        setTimeout(resolve, options.delay || 400)
      })
    }
    return Promise.resolve()
  }

  /**
   * Open annotation by ID (cursor integration)
   * @param {string} id - Annotation ID
   * @param {Object} options - Open options
   * @returns {Promise} - Promise that resolves when opened
   */
  async openAnnotation(id, options = {}) {
    const controller = this.findById(id)
    if (controller) {
      controller.open()
      this.activeAnnotations.add(controller)
      return new Promise((resolve) => {
        setTimeout(resolve, options.delay || 300)
      })
    }
    return Promise.resolve()
  }

  /**
   * Close annotation by ID (cursor integration)
   * @param {string} id - Annotation ID
   * @param {Object} options - Close options
   * @returns {Promise} - Promise that resolves when closed
   */
  async closeAnnotation(id, options = {}) {
    const controller = this.findById(id)
    if (controller) {
      controller.close()
      this.activeAnnotations.delete(controller)
      return new Promise((resolve) => {
        setTimeout(resolve, options.delay || 200)
      })
    }
    return Promise.resolve()
  }

  /**
   * Get annotation element by ID (cursor targeting)
   * @param {string} id - Annotation ID
   * @returns {HTMLElement|null} - Annotation element or null
   */
  getAnnotationElement(id) {
    const controller = this.findById(id)
    return controller ? controller.element : null
  }

  /**
   * Get annotation icon element by ID (cursor targeting)
   * @param {string} id - Annotation ID
   * @returns {HTMLElement|null} - Annotation icon element or null
   */
  getAnnotationIconElement(id) {
    const controller = this.findById(id)
    return controller ? controller.getCursorTarget() : null
  }

  /**
   * Track annotation state changes
   * @param {Object} controller - Annotation controller
   * @param {string} state - New state (opened/closed)
   */
  trackStateChange(controller, state) {
    if (state === 'opened') {
      this.activeAnnotations.add(controller)
    } else if (state === 'closed') {
      this.activeAnnotations.delete(controller)
    }
  }

  /**
   * Debug: Get all annotation info
   * @returns {Object} - Debug information
   */
  getDebugInfo() {
    return {
      total: this.annotations.size,
      visible: this.getVisibleAnnotations().length,
      hidden: this.getHiddenAnnotations().length,
      active: this.activeAnnotations.size,
      ids: Array.from(this.annotations.keys()),
      userAnnotations: this.userAnnotations.size,
    }
  }

  /**
   * Update positions for annotations that might be attached to an element
   * @param {HTMLElement} element - Element that was modified
   */
  updatePositionsForElement(element) {
    // Find all annotations that might be targeting this element or its children
    this.annotations.forEach((controller) => {
      if (controller.hasTargetValue && controller.targetValue) {
        const target = document.querySelector(controller.targetValue)
        // Update if the target is the element itself or a child of the element
        if (target && (target === element || element.contains(target) || target.contains(element))) {
          controller.updatePosition()
        }
      }
    })
  }

  // ===========================================
  // USER-CREATED ANNOTATION SYSTEM
  // ===========================================

  /**
   * Enter annotation mode - enables click-to-create functionality
   */
  enterAnnotationMode() {
    if (this.mode === 'annotate') return

    this.mode = 'annotate'

    // Add visual indicators
    document.body.classList.add('annotation-mode-active')

    // Add click listener for annotation creation
    document.addEventListener('click', this.handlePageClick, true)

    // Dispatch mode change event
    this.dispatchEvent('annotation:mode-change', { mode: 'annotate' })
  }

  /**
   * Exit annotation mode - disables click-to-create functionality
   */
  exitAnnotationMode() {
    if (this.mode === 'view') return

    this.mode = 'view'

    // Remove visual indicators
    document.body.classList.remove('annotation-mode-active')

    // Remove click listener
    document.removeEventListener('click', this.handlePageClick, true)

    // Dispatch mode change event
    this.dispatchEvent('annotation:mode-change', { mode: 'view' })
  }

  /**
   * Check if in annotation mode
   */
  isAnnotationMode() {
    return this.mode === 'annotate'
  }

  /**
   * Handle global keydown events
   */
  handleKeydown(event) {
    // Only handle escape key in annotation mode
    if (event.key === 'Escape' && this.mode === 'annotate') {
      event.preventDefault()
      this.exitAnnotationMode()
    }
  }

  /**
   * Handle clicks on annotation icons to ensure only one is open at a time
   */
  handleAnnotationClick(event) {
    // Don't interfere if we're in annotation creation mode
    if (this.mode === 'annotate') return

    // Don't interfere with send button clicks - they handle their own logic
    if (event.target.closest('.annotation__send-button')) {
      return
    }

    // Handle clicks outside annotations (close any open editing annotations)
    if (!event.target.closest('.annotation')) {
      this.handleClickOutsideAnnotations()
      return
    }

    // Only handle clicks on annotation icons
    const annotationIcon = event.target.closest('.annotation__icon')
    if (!annotationIcon) return

    // Find the parent annotation element
    const annotationElement = annotationIcon.closest('.annotation')
    if (!annotationElement) return

    // Check if this annotation is already active
    const isAlreadyActive = annotationElement.classList.contains('annotation--active')

    if (!isAlreadyActive) {
      // Close all other annotations before opening this one
      this.closeAll()

      // Small delay to let the closing animation start, then let the normal click handler proceed
      setTimeout(() => {
        // The normal click event will proceed to the annotation controller
      }, 50)
    }

    // Let the normal click event proceed to the annotation controller
  }

  /**
   * Handle clicks outside annotations
   */
  handleClickOutsideAnnotations() {
    // Find any open editing annotations and handle them
    const openAnnotations = document.querySelectorAll('.annotation--active')
    openAnnotations.forEach((annotation) => {
      const annotationId = annotation.getAttribute('data-annotation-id')
      if (annotationId && annotationId.startsWith('user-annotation-')) {
        this.handleAnnotationClose(annotationId)
      }
    })
  }

  /**
   * Handle page clicks in annotation mode
   */
  handlePageClick(event) {
    if (this.mode !== 'annotate') return

    // Ignore clicks on interactive elements
    if (this.isInteractiveElement(event.target)) {
      return
    }

    // Ignore clicks on existing annotations
    if (event.target.closest('[data-controller*="annotation"]')) {
      return
    }

    // Prevent default behavior and stop propagation
    event.preventDefault()
    event.stopPropagation()

    // Calculate click position
    const position = this.calculateClickPosition(event)

    // Create annotation
    this.createUserAnnotation(position)

    // Exit annotation mode after creating
    this.exitAnnotationMode()
  }

  /**
   * Check if element is interactive and should not receive annotations
   */
  isInteractiveElement(element) {
    // Basic interactive HTML elements
    const interactiveSelectors = [
      'button',
      'a[href]',
      'input',
      'textarea',
      'select',
      '[role="button"]',
      '.cursor',
      '.annotation',
    ]

    // Check if THIS element (not parents) has a data-action - these are clickable
    const hasDataAction = element.hasAttribute('data-action')

    // Check if element is part of cursor system
    const isCursorElement = element.closest('.cursor, .cursor-manager__container')

    const isInteractive =
      interactiveSelectors.some((selector) => element.matches(selector)) || hasDataAction || !!isCursorElement

    return isInteractive
  }

  /**
   * Calculate position for annotation based on click event
   */
  calculateClickPosition(event) {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    // Calculate position relative to document
    // We give annotation-enhanced controller document coordinates and let IT handle the context offset
    const documentPosition = {
      x: event.clientX + scrollLeft,
      y: event.clientY + scrollTop,
    }
    return documentPosition
  }

  /**
   * Create a new user annotation at the specified position
   */
  createUserAnnotation(position) {
    // Check annotation limit
    if (this.userAnnotations.size >= this.maxAnnotations) {
      return null
    }

    const id = `user-annotation-${this.annotationIdCounter++}`
    const annotation = {
      id,
      position,
      content: '', // Start with empty content for input mode
      createdAt: new Date(),
      isUserCreated: true,
      isEditing: true, // Start in editing mode
    }

    // Store annotation
    this.userAnnotations.set(id, annotation)

    // Render annotation
    this.renderUserAnnotation(annotation)

    // Dispatch creation event
    this.dispatchEvent('annotation:created', annotation)

    return annotation
  }

  /**
   * Remove user annotation by ID
   */
  removeUserAnnotation(id) {
    const annotation = this.userAnnotations.get(id)
    if (!annotation) return false

    // Remove from storage
    this.userAnnotations.delete(id)

    // Remove from DOM
    const element = document.querySelector(`[data-annotation-id="${id}"]`)
    if (element) {
      element.remove()
    }

    // Also unregister from controller registry if it exists
    this.annotations.delete(id)

    // Dispatch removal event
    this.dispatchEvent('annotation:removed', { id, annotation })

    return true
  }

  /**
   * Handle annotation closing - remove if in editing mode (not saved yet)
   */
  handleAnnotationClose(annotationId) {
    const annotation = this.userAnnotations.get(annotationId)
    if (!annotation) return

    // If annotation is in editing mode (not saved yet), always remove it
    if (annotation.isEditing) {
      this.removeUserAnnotation(annotationId)
    }
  }

  /**
   * Clear all user annotations (delete them)
   */
  clearAllUserAnnotations() {
    const annotationIds = Array.from(this.userAnnotations.keys())
    annotationIds.forEach((id) => this.removeUserAnnotation(id))

    this.dispatchEvent('annotation:cleared')
  }

  /**
   * Render user annotation content based on editing state
   */
  renderUserAnnotationContent(annotation) {
    if (annotation.isEditing) {
      // For new annotations in editing mode, only show close button if it has existing content
      const showCloseButton = annotation.content && annotation.content.trim().length > 0

      return `
        <div class="annotation__input-container">
          ${
            showCloseButton
              ? `
            <button class="annotation__close" onclick="window.annotationManager.handleRemoveAnnotation(event)" data-annotation-id="${annotation.id}" aria-label="Close annotation">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          `
              : ''
          }
          <div class="annotation__input-wrapper">
            <textarea
              class="annotation__input"
              placeholder="Write a comment..."
              data-annotation-id="${annotation.id}"
              onkeydown="window.annotationManager.handleInputKeydown(event)"
              oninput="window.annotationManager.resizeTextarea(this); window.annotationManager.updateSendButtonState(this)"
            >${annotation.content}</textarea>
            <button
              class="annotation__send-button"
              onclick="window.annotationManager.saveComment(event)"
              data-annotation-id="${annotation.id}"
              aria-label="Send comment"
              ${!annotation.content.trim() ? 'disabled' : ''}
            >
              <svg width="20px" height="20px" viewBox="0 0 20 20" version="1.1">
                <defs>
                    <path d="M0,0 L20,0 L20,20 L0,20 L0,0 Z" id="path-1"></path>
                </defs>
                <g id="icon-arrow-up-20" stroke="none" fill="none" xlink:href="#path-1" fill-rule="evenodd">
                    <use fill="#FFFFFF" xlink:href="#path-1"></use>
                    <g id="Group" stroke-width="1" transform="translate(6, 5.25)" fill="currentColor">
                        <g transform="translate(-4, -3)" id="Rectangle-2">
                            <path d="M8.53033009,3.46966991 L11.5303301,6.46966991 C11.8232233,6.76256313 11.8232233,7.23743687 11.5303301,7.53033009 C11.2374369,7.8232233 10.7625631,7.8232233 10.4696699,7.53033009 L8.75,5.81 L8.75,11.9558824 C8.75,12.3944614 8.41421356,12.75 8,12.75 C7.58578644,12.75 7.25,12.3944614 7.25,11.9558824 L7.25,5.81 L5.53033009,7.53033009 C5.26406352,7.79659665 4.84739984,7.8208027 4.55378835,7.60294824 L4.46966991,7.53033009 C4.1767767,7.23743687 4.1767767,6.76256313 4.46966991,6.46966991 L7.46966991,3.46966991 C7.76256313,3.1767767 8.23743687,3.1767767 8.53033009,3.46966991 Z"></path>
                        </g>
                    </g>
                </g>
              </svg>
            </button>
          </div>
        </div>
      `
    }

    // View mode - show saved content with proper comment structure
    return `
      <header class="annotation__header">
        <li class="annotation__comment">
          <div class="annotation__meta">
            <div class="annotation__avatar-placeholder"></div>
            <p class="annotation__author">You</p>
            <p class="annotation__timestamp">Just now</p>
            <button class="annotation__close" onclick="window.annotationManager.handleRemoveAnnotation(event)" data-annotation-id="${annotation.id}" aria-label="Close annotation">
              <svg width="12" height="12" viewBox="0 0 12 12">
                <path d="M1 1l10 10M1 11L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
              </svg>
            </button>
          </div>
          <div class="annotation__text">${this.escapeHtml(annotation.content)}</div>
        </li>
      </header>
    `
  }

  /**
   * Render user annotation in the DOM
   * @param {Object} annotation - Annotation data
   * @param {boolean} autoOpen - Whether to automatically open the annotation (default: true)
   */
  renderUserAnnotation(annotation, autoOpen = true) {
    // Find container for annotations
    let container = document.querySelector('.user-annotations-container')

    if (!container) {
      // Create container if it doesn't exist
      container = document.createElement('div')
      container.className = 'user-annotations-container'
      container.style.cssText = 'position: relative; pointer-events: none; z-index: 1000;'

      // Insert into appropriate parent
      const collaboratePage = document.querySelector('.collaborate-page')
      const target = collaboratePage || document.body
      target.appendChild(container)
    }

    // Create annotation HTML (identical to standard annotation-enhanced structure)
    const annotationHTML = `
      <div class="annotation annotation-enhanced annotation--draft"
           data-controller="annotation-enhanced"
           data-annotation-enhanced-id-value="${annotation.id}"
           data-annotation-enhanced-position-value='${JSON.stringify(annotation.position)}'
           data-annotation-enhanced-visibility-value="visible"
           data-annotation-id="${annotation.id}"
           style="position: absolute; left: ${annotation.position.x}px; top: ${annotation.position.y}px;">

        <div class="annotation__icon"
             data-annotation-enhanced-target="icon"
             data-action="click->annotation-enhanced#handleClick">
        </div>

        <div class="annotation__popup entrance"
             data-controller="entrance"
             data-annotation-enhanced-target="popup">
          ${this.renderUserAnnotationContent(annotation)}
        </div>
      </div>
    `

    // Insert into container
    container.insertAdjacentHTML('beforeend', annotationHTML)

    // Get the newly created element
    const annotationElement = container.lastElementChild

    // Connect Stimulus controllers and register with annotation manager
    if (window.application) {
      // Let Stimulus connect first
      setTimeout(() => {
        const controller = this.application.getControllerForElementAndIdentifier(
          annotationElement,
          'annotation-enhanced'
        )
        if (controller) {
          // Register the controller
          this.register(controller)
          // Only open if autoOpen is true
          if (autoOpen) {
            controller.open()
          }
        }
      }, 0)
    } else {
      // Fallback: only open if autoOpen is true
      if (autoOpen) {
        setTimeout(() => {
          annotationElement.classList.add('annotation--active')
          const textarea = annotationElement.querySelector('textarea')
          if (textarea) {
            textarea.focus()
            this.resizeTextarea(textarea)
          }
        }, 0)
      }
    }
  }

  /**
   * Handle keyboard input in annotation textarea
   */
  handleInputKeydown(event) {
    // Save on Cmd/Ctrl + Enter
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      this.saveComment(event)
      return
    }

    // Auto-resize textarea and update button state on input
    setTimeout(() => {
      this.resizeTextarea(event.target)
      this.updateSendButtonState(event.target)
    }, 0)
  }

  /**
   * Update send button state based on textarea content
   */
  updateSendButtonState(textarea) {
    if (!textarea) return

    const annotationId = textarea.dataset.annotationId
    if (!annotationId) return

    const sendButton = document.querySelector(`button[data-annotation-id="${annotationId}"].annotation__send-button`)
    if (!sendButton) return

    const hasContent = textarea.value.trim().length > 0
    if (hasContent) {
      sendButton.removeAttribute('disabled')
    } else {
      sendButton.setAttribute('disabled', '')
    }
  }

  /**
   * Auto-resize textarea based on content
   */
  resizeTextarea(textarea) {
    if (!textarea) return

    const defaultHeight = '32px'

    // Reset height to get accurate scrollHeight
    textarea.style.height = defaultHeight

    // If textarea is empty or just whitespace, keep it at single line height
    if (!textarea.value.trim()) {
      textarea.style.height = defaultHeight
      return
    }

    // Check if content contains line breaks (multiple lines)
    const hasLineBreaks = textarea.value.includes('\n')

    if (!hasLineBreaks) {
      // Single line - check if it overflows
      const isOverflowing = textarea.scrollWidth > textarea.clientWidth
      if (!isOverflowing) {
        textarea.style.height = defaultHeight
        return
      }
    }

    // Multiple lines - calculate based on scroll height
    textarea.style.height = 'auto'
    const newHeight = Math.max(24, Math.min(textarea.scrollHeight, 120))

    textarea.style.height = `${newHeight}px`
  }

  /**
   * Save comment from input and switch to view mode
   */
  saveComment(event) {
    // Prevent the event from bubbling up to avoid triggering other click handlers
    event.preventDefault()
    event.stopPropagation()

    const annotationId = event.currentTarget.dataset.annotationId
    const annotation = this.userAnnotations.get(annotationId)

    if (!annotation) return

    // Get the input value
    const input = document.querySelector(`textarea[data-annotation-id="${annotationId}"]`)
    const content = input ? input.value.trim() : ''

    if (!content) {
      this.removeUserAnnotation(annotationId)
      return
    }

    // Update annotation data
    annotation.content = content
    annotation.isEditing = false
    this.userAnnotations.set(annotationId, annotation)

    // Remove draft class since annotation is now saved
    const annotationElement = document.querySelector(`[data-annotation-id="${annotationId}"]`)
    if (annotationElement) {
      annotationElement.classList.remove('annotation--draft')
      annotationElement.classList.add('annotation--was-active')
    }

    // Re-render annotation in view mode
    this.updateUserAnnotationDisplay(annotationId)

    // Keep annotation open in view mode
    this.keepAnnotationOpen(annotationId)
  }

  /**
   * Update user annotation display after editing
   */
  updateUserAnnotationDisplay(annotationId) {
    const annotation = this.userAnnotations.get(annotationId)
    if (!annotation) return

    const annotationElement = document.querySelector(`[data-annotation-id="${annotationId}"]`)
    if (!annotationElement) return

    const popup = annotationElement.querySelector('.annotation__popup')
    if (!popup) return

    // Replace popup content
    popup.innerHTML = this.renderUserAnnotationContent(annotation)
  }

  /**
   * Keep annotation open after saving comment
   */
  // eslint-disable-next-line class-methods-use-this
  keepAnnotationOpen(annotationId) {
    setTimeout(() => {
      const annotationElement = document.querySelector(`[data-annotation-id="${annotationId}"]`)
      if (annotationElement) {
        annotationElement.classList.add('annotation--active')
      }
    }, 50) // Small delay to ensure DOM is updated
  }

  /**
   * Handle close button clicks (called from DOM event)
   */
  handleRemoveAnnotation(event) {
    const annotationId = event.currentTarget.dataset.annotationId
    if (annotationId) {
      this.removeUserAnnotation(annotationId)
    }
  }

  /**
   * Get all user annotations
   */
  getUserAnnotations() {
    return Array.from(this.userAnnotations.values())
  }

  /**
   * Get user annotation by ID
   */
  getUserAnnotation(id) {
    return this.userAnnotations.get(id)
  }

  /**
   * Escape HTML for safe rendering
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Dispatch custom events
   */
  dispatchEvent(eventType, detail = {}) {
    const event = new CustomEvent(eventType, {
      detail,
      bubbles: true,
      cancelable: true,
    })
    document.dispatchEvent(event)
  }

  // ===========================================
  // BACKWARD COMPATIBILITY METHODS
  // ===========================================

  /**
   * Backward compatibility: Alias for getUserAnnotations
   */
  getAnnotations() {
    return this.getUserAnnotations()
  }

  /**
   * Backward compatibility: Alias for getUserAnnotation
   */
  getAnnotation(id) {
    return this.getUserAnnotation(id)
  }

  /**
   * Backward compatibility: Alias for removeUserAnnotation
   */
  removeAnnotation(id) {
    return this.removeUserAnnotation(id)
  }

  /**
   * Backward compatibility: Alias for clearAllUserAnnotations
   */
  clearAllAnnotations() {
    return this.clearAllUserAnnotations()
  }

  /**
   * Backward compatibility: Alias for createUserAnnotation
   */
  createAnnotation(position) {
    return this.createUserAnnotation(position)
  }

  /**
   * Backward compatibility: Enhanced closeAll that handles both types
   * This is already implemented above but documented here for clarity
   */
  closeAllAnnotations() {
    return this.closeAll()
  }

  /**
   * Initialize method for backward compatibility with UserAnnotationManager
   */
  static initialize() {
    // Create a new instance if none exists
    if (!window.annotationManager) {
      const manager = new AnnotationManager()
      manager.initialize()
      return manager
    }
    return window.annotationManager
  }
}

// Export the class (not an instance)
export default AnnotationManager
